import React, { useEffect } from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Launch, Create } from "@mui/icons-material";
import GoldenSwitch from "../../goldenSwitch/GoldenSwitch";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { myExpariencesService } from "../../../services";
import {
  moveElement,
  updateElement,
  updateModalManager,
  updateMultiSelection,
} from "../../../reducers/configurator/configuratorSlice";
import SearchForm from "../../forms/SearchForm";
import TranslationsEditor from "../../translationsEditor/TranslationsEditor";
import VrWatermark from "../../vrWatermark/VrWatermark";
import clsx from "clsx";
import { useViewport } from "../../../helpers";
import { resetLayers } from "../../../reducers/configurator/utils";
import preventDoubleTrad from "../../../helpers/prevent-double-trad";
import ConfigDataBank from "./ConfigDataBank";

const PREFIX = "FrameForm";

const classes = {
  root: `${PREFIX}-root`,
  searchTargetsContainer: `${PREFIX}-searchTargetsContainer`,
  targetsResultContainer: `${PREFIX}-targetsResultContainer`,
  singleTargetResultContainer: `${PREFIX}-singleTargetResultContainer`,
  previewPaintingResearch: `${PREFIX}-previewPaintingResearch`,
  exparienceLogoContainer: `${PREFIX}-exparienceLogoContainer`,
  paintingContainerContent: `${PREFIX}-paintingContainerContent`,
  paintingFileName: `${PREFIX}-paintingFileName`,
  marginBottom: `${PREFIX}-marginBottom`,
  marginTop: `${PREFIX}-marginTop`,
  marginRight: `${PREFIX}-marginRight`,
  visualisationIconSize: `${PREFIX}-visualisationIconSize`,
  selectFlag: `${PREFIX}-selectFlag`,
  fetchingContainer: `${PREFIX}-fetchingContainer`,
  selectMoldContainer: `${PREFIX}-selectMoldContainer`,
  selectSizeContainer: `${PREFIX}-selectSizeContainer`,
  selectSizeFull: `${PREFIX}-selectSizeFull`,
  selectSize: `${PREFIX}-selectSize`,
  flexContainer: `${PREFIX}-flexContainer`,
  modifyCustomIcon: `${PREFIX}-modifyCustomIcon`,
  dialogPadding: `${PREFIX}-dialogPadding`,
  pedestalButtonsContainer: `${PREFIX}-pedestalButtonsContainer`,
  minWidthPedestal: `${PREFIX}-minWidthPedestal`,
  externalAccordionBorders: `${PREFIX}-externalAccordionBorders`,
  visiblePaintingContainer: `${PREFIX}-visiblePaintingContainer`,
  dividerStyle: `${PREFIX}-dividerStyle`,
};

const FrameFormContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      padding: "1rem 0",
      maxWidth: "850px",
      margin: "0 auto",
    },
    [`& .${classes.searchTargetsContainer}`]: {
      margin: "1rem 0",
      position: "relative",
    },
    [`& .${classes.targetsResultContainer}`]: {
      height: "175px",
      overflowY: "auto",
      backgroundColor: "rgb(255, 255, 255, 0.2)",
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
    },
    [`& .${classes.singleTargetResultContainer}`]: {
      height: "60px",
      padding: "0.5rem",
      display: "flex",
      "&:hover": {
        backgroundColor: "grey",
        cursor: "pointer",
      },
    },
    [`& .${classes.previewPaintingResearch}`]: {
      width: "100%",
      maxWidth: "40px",
      objectFit: "cover",
      margin: "0",
    },
    [`& .${classes.exparienceLogoContainer}`]: {
      width: "100%",
      objectFit: "cover",
      margin: "0",
      position: "relative",
      left: "50%",
      transform: "translateX(-50%)",
    },
    [`& .${classes.paintingContainerContent}`]: {
      display: "flex",
      flexDirection: "row",
    },
    [`& .${classes.paintingFileName}`]: {
      transform: "translateY(9px)",
      marginLeft: "1rem",
      textOverflow: "ellipsis",
      maxWidth: "150px",
      [theme.breakpoints.between("xs", "sm")]: {
        maxWidth: "100px",
      },
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    [`& .${classes.marginBottom}`]: {
      marginBottom: "1rem",
    },
    [`& .${classes.marginTop}`]: {
      marginTop: "1rem",
    },
    [`& .${classes.marginRight}`]: {
      marginRight: "1rem",
    },
    [`& .${classes.visualisationIconSize}`]: {
      width: "50px",
    },
    [`& .${classes.selectFlag}`]: {
      "& > button": {
        background: theme.palette.primary.main,
        maxWidth: "200px",
        "& > span": {
          color: "#303030",
        },
      },
      "& > ul": {
        background: "#424242",
        maxHeight: "150px",
        maxWidth: "200px",
        "& > div": {
          background: "#424242",
        },
      },
    },
    [`& .${classes.fetchingContainer}`]: {
      textAlign: "center",
    },
    [`& .${classes.selectMoldContainer}`]: {
      marginTop: "2rem",
      width: "100%",
    },
    [`& .${classes.selectSizeContainer}`]: {
      margin: "1rem 0 0 0",
      width: "100%",
    },
    [`& .${classes.selectSizeFull}`]: {
      width: "100%",
    },
    [`& .${classes.selectSize}`]: {
      width: "calc(100% - 50px - 1rem)",
      marginRight: "1rem",
    },
    [`& .${classes.flexContainer}`]: {
      display: "flex",
      flexDirection: "row",
    },
    [`& .${classes.modifyCustomIcon}`]: {
      width: "50px",
    },
    [`& .${classes.dialogPadding}`]: {
      padding: "16px 24px",
      width: "100%",
      minWidth: "250px",
      maxWidth: "650px",
    },
    [`& .${classes.pedestalButtonsContainer}`]: {
      display: "flex",
      justifyContent: "space-around",
    },
    [`& .${classes.minWidthPedestal}`]: {
      minWidth: "350px",
    },
    [`& .${classes.externalAccordionBorders}`]: {
      border: "1px solid rgba(211, 175, 95, 0.3)",
      "& > div": {
        boxShadow: "none",
      },
    },
    [`& .${classes.visiblePaintingContainer}`]: {
      margin: "1rem 0 1.5rem 0",
      display: "flex",
      justifyContent: "left",
      paddingLeft: "0.5rem",
    },
    [`& .${classes.dividerStyle}`]: {
      height: "1px",
      backgroundColor: "grey",
      opacity: "0.8",
      marginBottom: "1rem",
    },
  };
});

const FrameForm = ({ layer }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { width } = useViewport();
  const [searchState, setSearchState] = useState({
    searchContainer: false,
    searchInput: "",
    searchResults: null,
    searchTargets: "",
  });
  const isAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.role
  );
  const multimode = useSelector((state) => state.configurator?.multiSelection);
  const selectedTile = useSelector((state) => state.configurator.selectedTile);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const myTile = useSelector((state) =>
    selectedTile
      ? state.configurator.grid[parentLayout][selectedTile.row][
          selectedTile.column
        ]
      : null
  );
  const saveTrigger = useSelector(
    (state) => state.configurator.modalManager.saveTrigger
  );
  const [frameState, setFrameState] = useState({});
  const [isAIActive, setIsAIActive] = useState(false);

  console.log(frameState);

  useEffect(() => {
    if (!multimode.active && multimode.data) {
      setFrameState(multimode.data);
      dispatch(updateMultiSelection(resetLayers.multiSelection));
    }
  }, [multimode.active, multimode.data]);

  useEffect(() => {
    if ((frameState.file || frameState.imageUrl) && checkExternalLinks()) {
      dispatch(updateModalManager({ disableSave: false }));
    } else {
      dispatch(updateModalManager({ disableSave: true }));
    }
  }, [frameState.file, frameState.imageUrl, frameState.externalLinks]);

  useEffect(() => {
    if (saveTrigger) {
      if (
        frameState?.hasOwnProperty("startTile") &&
        frameState?.hasOwnProperty("endTile")
      ) {
        const tmpTile = myTile[frameState.rotation];
        const sizeChanged =
          frameState.size !== "custom" && tmpTile.size === "custom";
        if (layer !== frameState.rotation || sizeChanged) {
          const oldTileStart = sizeChanged
            ? tmpTile.startTile
            : { row: selectedTile.row, column: selectedTile.column };
          const oldTileEnd = sizeChanged
            ? tmpTile.endTile
            : {
                row: frameState.endTile.row,
                column: frameState.endTile.column,
              };
          const newTileStart = sizeChanged
            ? { row: selectedTile.row, column: selectedTile.column }
            : {
                row: frameState.startTile.row,
                column: frameState.startTile.column,
              };
          const newTileEnd = sizeChanged
            ? { row: selectedTile.row, column: selectedTile.column }
            : {
                row: frameState.endTile.row,
                column: frameState.endTile.column,
              };
          // changement orientation de frame custom non implémenté
          frameState.startTile = newTileStart;
          frameState.endTile = newTileEnd;
          dispatch(
            moveElement({
              oldLayer: layer,
              layer: frameState.rotation,
              data: frameState,
              tile: oldTileStart,
              tileEnd: oldTileEnd,
              newTileStart,
              newTileEnd,
            })
          );
        }
        dispatch(
          updateElement({
            layer: frameState.rotation,
            data: frameState,
            tile: {
              row: frameState.startTile.row,
              column: frameState.startTile.column,
            },
            tileEnd: {
              row: frameState.endTile.row,
              column: frameState.endTile.column,
            },
          })
        );
      } else {
        if (layer !== frameState.rotation) {
          dispatch(
            moveElement({
              oldLayer: layer,
              layer: frameState.rotation,
              data: frameState,
              tile: { row: selectedTile.row, column: selectedTile.column },
              newTile: { row: selectedTile.row, column: selectedTile.column },
            })
          );
        }
        dispatch(
          updateElement({
            layer: frameState.rotation,
            data: frameState,
            tile: { row: selectedTile.row, column: selectedTile.column },
          })
        );
      }
      dispatch(updateModalManager(resetLayers.modalManager));
    }
  }, [saveTrigger]);

  useEffect(() => {
    if (!multimode.data) {
      setFrameState({
        rotation: layer,
        layout: parentLayout,
        visible: true,
        visibleRoom: true,
        molding: "design",
        size: "medium",
        link: "",
        title: "",
        externalLinks: [],
        description: {
          initial: "",
          translations: [],
        },
        ...myTile[layer],
        type: "frame",
      });
    }
  }, [selectedTile.row, selectedTile.column]);

  const checkExternalLinks = () => {
    let result = true;
    if (frameState.externalLinks?.length === 0) return result;
    else {
      frameState.externalLinks?.forEach((item) => {
        if (!item.link || !item.logo || !item.title) {
          result = false;
        }
      });
    }
    return result;
  };

  function handlePaintingTranslationChange(value, key, transIndex) {
    let tmp = { ...frameState };
    const tmp2 = [...tmp.description.translations];
    tmp2[transIndex] = { ...tmp2[transIndex], [key]: value };

    tmp.description = {
      ...tmp.description,
      translations: tmp2,
    };
    setFrameState(tmp);
  }

  function handleDeleteTranslation(transIndex) {
    let tmp = { ...frameState };
    const tmp2 = [...tmp.description.translations];
    tmp2.splice(transIndex, 1);
    tmp.description = {
      ...tmp.description,
      translations: tmp2,
    };
    setFrameState(tmp);
  }

  const handleMoveTraduction = (fromIndex, toIndex) => {
    if (toIndex >= 0 && toIndex < frameState.description.translations.length) {
      let tmp = { ...frameState };
      const myObj = [...tmp.description.translations];
      const tmpElem = myObj[fromIndex];
      myObj.splice(fromIndex, 1);
      myObj.splice(toIndex, 0, tmpElem);
      tmp = {
        ...tmp,
        description: { ...tmp.description, translations: myObj },
      };
      setFrameState(tmp);
    }
  };

  const handlePaintingChange = (value, key) => {
    let tmp = { ...frameState };
    if (key === "translations" || key === "description") {
      if (key === "translations") {
        const myArr = [...tmp.description[key]];
        const newArr = preventDoubleTrad(myArr, value);
        tmp["description"] = { ...tmp.description, [key]: newArr };
        setFrameState(tmp);
      } else {
        tmp[key] = { ...tmp[key], initial: value };
        setFrameState(tmp);
      }
    } else {
      tmp[key] = value;
      if (key === "file") tmp.previewUrl = URL.createObjectURL(value);
      // remove endTile if size is different from custom (case where size change from custom to other)
      if (
        key === "size" &&
        value !== "custom" &&
        tmp.hasOwnProperty("endTile")
      ) {
        delete tmp.endTile;
      }
      setFrameState(tmp);
      if (key === "size" && value === "custom") {
        dispatch(
          updateMultiSelection({
            active: true,
            x: ["north", "south"].includes(tmp.rotation) ? -1 : 0,
            y: ["east", "west"].includes(tmp.rotation) ? -1 : 0,
            tile: selectedTile,
            data: tmp,
            layer: tmp.rotation,
          })
        );
        dispatch(updateModalManager({ open: false }));
      }
    }
  };

  const searchTargets = (inputValue) => {
    if (inputValue && inputValue !== "") {
      let tmp = { ...searchState };
      myExpariencesService.loadTargets(inputValue).then((res) => {
        tmp["searchResults"] = res;
        tmp["searchInput"] = inputValue;
        tmp["searchContainer"] = true;
        setSearchState(tmp);
      });
    } else if (!inputValue || inputValue === "") {
      let tmp = { ...searchState };
      tmp["searchInput"] = "";
      tmp["searchContainer"] = false;
      tmp["searchResults"] = null;
      setSearchState(tmp);
    }
  };

  const selectTargetFromSearch = (e, result) => {
    e.stopPropagation();
    setFrameState({
      ...frameState,
      file: null,
      providerId: result.blinklProvider,
      campaignId: result.blinklCampaign,
      searchTargets: result.label,
      buyable: false,
      purchasePrice: 0,
      imageUrl: result.imageBorderlessUrl || result.imageUrl,
      searchContainer: false,
      description: {
        initial: "",
        translations: [],
      },
    });
    let tmp = {
      ...searchState,
      searchContainer: false,
    };
    setSearchState(tmp);
  };

  useEffect(() => {
    if (frameState && (frameState.promptAI || frameState.introductionAI)) {
      setIsAIActive(true);
    }
  }, [frameState]);

  if (Object.keys(frameState).length === 0) {
    return null;
  } else
    return (
      <FrameFormContainer>
        <Box className={classes.root}>
          <Typography variant="body2">
            {t("configurator.paintingChoice")}
          </Typography>
          <ClickAwayListener
            onClickAway={(e) => {
              setSearchState({ ...searchState, searchContainer: false });
            }}
          >
            <Box className={classes.searchTargetsContainer}>
              <Box
                style={{
                  marginLeft: "5px",
                  width: "100%",
                  marginBottom: "0.5rem",
                }}
              >
                <SearchForm
                  search={(inputValue) => searchTargets(inputValue)}
                  searchValue={searchState.searchInput || ""}
                  onFocusInput={(e) => {
                    e.stopPropagation();
                    setSearchState({ ...searchState, searchContainer: true });
                  }}
                />
              </Box>
              {searchState.searchInput &&
                searchState.searchInput !== "" &&
                searchState.searchContainer && (
                  <Box className={classes.targetsResultContainer}>
                    {searchState.searchResults &&
                      searchState.searchResults.map((result, resultIndex) => {
                        return (
                          <Box
                            key={`${resultIndex + 1}`}
                            className={classes.singleTargetResultContainer}
                            onClick={(e) => selectTargetFromSearch(e, result)}
                          >
                            <img
                              id={`preview-painting-research-${result.label}`}
                              className={classes.previewPaintingResearch}
                              alt="preview-result"
                              src={result.imageBorderlessUrl || result.imageUrl}
                            />
                            <Typography
                              variant="body1"
                              style={{ padding: "0.5rem" }}
                            >
                              {result.label}-{result.frameLabel}
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>
                )}
            </Box>
          </ClickAwayListener>
          <ConfigDataBank
            mode="frames"
            onSelectMedia={(media) => {
              if (media.file) {
                setFrameState({
                  ...frameState,
                  previewUrl: media.previewUrl,
                  file: media.file,
                });
              } else
                setFrameState({
                  ...frameState,
                  imageUrl: media.imageUrl,
                });
            }}
          />
          <img
            id={`painting-file`}
            className={classes.exparienceLogoContainer}
            alt={
              frameState.file || (!frameState.file && frameState.imageUrl)
                ? `painting-file`
                : null
            }
            src={
              frameState.file
                ? URL.createObjectURL(frameState.file)
                : frameState.imageUrl
                ? frameState.imageUrl
                : undefined
            }
            style={{
              margin: (frameState.file || frameState.imageUrl) && "0 0 1rem 0",
              width:
                frameState.size === "small"
                  ? "30%"
                  : frameState.size === "medium"
                  ? "60%"
                  : "100%",
            }}
          />
          <Box className={classes.paintingContainerContent}>
            <Button
              variant="contained"
              component="label"
              color="primary"
              onChange={(e) => {
                if (e.target.files[0] !== undefined) {
                  handlePaintingChange(e.target.files[0], "file");
                } else {
                  return null;
                }
              }}
            >
              {t("myDesign.zones.uploadVideo.selectFileButton")}
              <input
                type="file"
                accept="image/png, image/jpeg, image/gif, image/apng, image/avif, image/svg"
                hidden
              />
            </Button>
            <Typography variant="body1" className={classes.paintingFileName}>
              {frameState.file && frameState.file !== null
                ? frameState.file.name
                : ""}
            </Typography>
          </Box>
          <TextField
            name={`frame-title`}
            className={classes.marginTop}
            label={t("myExpariences.form.title")}
            value={frameState.title}
            onChange={(e) => handlePaintingChange(e.target.value, "title")}
            fullWidth
            variant={"outlined"}
          />
          <FormControl
            variant="outlined"
            className={clsx(classes.selectSizeContainer, classes.flexContainer)}
          >
            <InputLabel id="size-simple-select-outlined-label">
              {t("myExpariences.form.size")}
            </InputLabel>
            <Select
              className={
                frameState.size === "custom"
                  ? classes.selectSize
                  : classes.selectSizeFull
              }
              labelId="size-simple-select-outlined-label"
              id="size-simple-select-outlined"
              value={frameState.size ? frameState.size : "medium"}
              onChange={(e) => handlePaintingChange(e.target.value, "size")}
              label={t("myExpariences.form.size")}
            >
              <MenuItem value="small">{t("myExpariences.form.small")}</MenuItem>
              <MenuItem value="medium">
                {t("myExpariences.form.medium")}
              </MenuItem>
              <MenuItem value="large">{t("myExpariences.form.large")}</MenuItem>
              <MenuItem value="custom">
                {t("myExpariences.form.custom")}
              </MenuItem>
            </Select>
            {frameState.size === "custom" && (
              <IconButton
                className={classes.modifyCustomIcon}
                color="primary"
                onClick={() => handlePaintingChange("custom", "size")}
              >
                <Create />
              </IconButton>
            )}
          </FormControl>
          <FormControl
            variant="outlined"
            className={classes.selectMoldContainer}
          >
            <InputLabel id="facade-simple-select-outlined-label">
              {t("configurator.facade")}
            </InputLabel>
            <Select
              labelId="facade-simple-select-outlined-label"
              id="facade-simple-select-outlined"
              value={frameState.rotation || ""}
              onChange={(e) => {
                setFrameState({ ...frameState, rotation: e.target.value });
              }}
              label={t("configurator.facade")}
              disabled={frameState.size === "custom"}
            >
              <MenuItem
                value="north"
                disabled={frameState.rotation === "north"}
              >
                {t("configurator.north")}
              </MenuItem>
              <MenuItem value="east" disabled={frameState.rotation === "east"}>
                {t("configurator.east")}
              </MenuItem>
              <MenuItem
                value="south"
                disabled={frameState.rotation === "south"}
              >
                {t("configurator.south")}
              </MenuItem>
              <MenuItem value="west" disabled={frameState.rotation === "west"}>
                {t("configurator.west")}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            className={classes.selectMoldContainer}
          >
            <InputLabel id="molding-simple-select-outlined-label">
              {t("myExpariences.form.molding")}
            </InputLabel>
            <Select
              labelId="molding-simple-select-outlined-label"
              id="molding-simple-select-outlined"
              value={frameState.molding || "design"}
              onChange={(e) => handlePaintingChange(e.target.value, "molding")}
              label={t("myExpariences.form.molding")}
            >
              <MenuItem value="design">
                {t("myExpariences.form.design")}
              </MenuItem>
              <MenuItem value="oak">{t("myExpariences.form.oak")}</MenuItem>
              <MenuItem value="wood">{t("myExpariences.form.ipe")}</MenuItem>
              <MenuItem value="white">{t("myExpariences.form.white")}</MenuItem>
              <MenuItem value="versailles">
                {t("myExpariences.form.versailles")}
              </MenuItem>
              <MenuItem value="versaillesblanc">
                {t("myExpariences.form.versailleswhite")}
              </MenuItem>
            </Select>
          </FormControl>
          <Box className={clsx(classes.marginTop, classes.marginBottom)}>
            <Box className={classes.paintingContainerContent}>
              <TextField
                name={`link-painting`}
                label={t("myExpariences.form.linkPainting")}
                value={frameState.link || ""}
                onChange={(e) => handlePaintingChange(e.target.value, "link")}
                className={classes.marginRight}
                fullWidth
                variant={"outlined"}
              />
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="span"
                className={classes.visualisationIconSize}
                onClick={() => window.open(frameState.link, "_blank")}
              >
                <Launch />
              </IconButton>
            </Box>
          </Box>
          <Typography variant="body1" className={classes.marginBottom}>
            {t("myExpariences.form.visibleForWebVR")}
          </Typography>
          <Box className={classes.visiblePaintingContainer}>
            <GoldenSwitch
              checked={frameState.visible}
              onChange={(e) => {
                handlePaintingChange(e.target.checked, "visible");
              }}
              disabled={false}
              label={
                frameState.visible
                  ? t("myExpariences.form.visibleForWebVRSwitchOn")
                  : t("myExpariences.form.visibleForWebVRSwitchOff")
              }
            />
          </Box>
          <Typography variant="body1" className={classes.marginBottom}>
            {t("myExpariences.form.ARdescription")}
          </Typography>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              marginBottom: "1rem",
            }}
          >
            <Box>
              <TranslationsEditor
                elements={frameState?.description?.translations || []}
                defaultText={frameState?.description?.initial || ""}
                defaultTextKey="description"
                handleDefaultText={handlePaintingChange}
                addTranslation={handlePaintingChange}
                removeTranslation={handleDeleteTranslation}
                handleTranslation={handlePaintingTranslationChange}
                configurator
                translate
                moveTraduction={handleMoveTraduction}
              />
            </Box>
          </Box>
          {isAdmin && (
            <Box>
              <Divider className={classes.dividerStyle} />
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-around" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color={"primary"}
                        onChange={(e) => {
                          setIsAIActive(e.target.checked);
                        }}
                        checked={isAIActive}
                      />
                    }
                    label={t("myExpariences.form.activateAI")}
                    className={classes.marginBottom}
                    name={`is-AI-active`}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        color={"primary"}
                        onChange={(e) => {
                          handlePaintingChange(e.target.checked, "isWebXrAI");
                        }}
                        checked={frameState.isWebXrAI}
                      />
                    }
                    label={t("myExpariences.form.activateWebAI")}
                    className={classes.marginBottom}
                    name={`is-webxr-AI-active`}
                  />
                </Box>
                {(isAIActive || frameState.isWebXrAI) && (
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <TextField
                      id="prompt AI"
                      label={t("myExpariences.form.promptAI")}
                      value={frameState.promptAI}
                      className={classes.marginBottom}
                      name={`prompt-AI`}
                      multiline
                      rows={5}
                      onChange={(e) => {
                        handlePaintingChange(e.target.value, "promptAI");
                      }}
                      variant={"outlined"}
                    />
                    <TextField
                      id="introduction AI"
                      label={t("myExpariences.form.introductionAI")}
                      value={frameState.introductionAI}
                      className={classes.marginBottom}
                      name={`introduction-AI`}
                      multiline
                      rows={5}
                      onChange={(e) => {
                        handlePaintingChange(e.target.value, "introductionAI");
                      }}
                      variant={"outlined"}
                    />
                  </Box>
                )}
              </Box>
              <Divider className={classes.dividerStyle} />

              <FormControlLabel
                control={
                  <Checkbox
                    color={"primary"}
                    onChange={(e) =>
                      handlePaintingChange(e.target.checked, "buyable")
                    }
                    checked={frameState.buyable || false}
                  />
                }
                label={t("myExpariences.form.buyable")}
                className={classes.marginBottom}
                name={`buyable-painting`}
              />
              {frameState.buyable && (
                <TextField
                  id="purchase-price-number"
                  label={t("myExpariences.form.purchasePrice")}
                  type="number"
                  defaultValue={frameState.purchasePrice}
                  className={classes.marginBottom}
                  name={`purchase-price-painting`}
                  onChange={(e) =>
                    handlePaintingChange(e.target.value, "purchasePrice")
                  }
                  variant={"outlined"}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            </Box>
          )}
          <Typography variant="body2" className={classes.marginBottom}>
            {t("myExpariences.form.multizoneExplanations")}
          </Typography>
          <Typography
            variant="body2"
            className={classes.marginBottom}
            style={{ fontStyle: "italic" }}
          >
            {t("myExpariences.form.multizoneExplanationsContact")}
            <a
              href={`mailto:${t(
                "myExpariences.form.multizoneExplanationsMail"
              )}`}
              style={{ color: "#D3AF5F" }}
            >
              {t("myExpariences.form.multizoneExplanationsMail")}
            </a>
            {t("myExpariences.form.multizoneExplanationsEnd")}
          </Typography>
          <Box className={classes.marginBottom}>
            <TextField
              name={`campaign-id-painting`}
              label={t("myExpariences.form.campaignIdPainting")}
              value={frameState.campaignId || ""}
              onChange={(e) =>
                handlePaintingChange(e.target.value, "campaignId")
              }
              disabled={!isAdmin}
              fullWidth
              variant={"outlined"}
            />
          </Box>
          <TextField
            name={`provider-id-painting`}
            label={t("myExpariences.form.providerId")}
            value={frameState.providerId || ""}
            onChange={(e) => handlePaintingChange(e.target.value, "providerId")}
            disabled={!isAdmin}
            fullWidth
            variant={"outlined"}
          />
          <Divider className={clsx(classes.marginTop, classes.dividerStyle)} />
          <Typography variant="body1" className={classes.marginBottom}>
            {t("myExpariences.form.webVrExternalLinks")}
          </Typography>
          <Box
            className={clsx(
              classes.marginBottom,
              classes.externalAccordionBorders
            )}
          >
            <VrWatermark
              open={true}
              externalLinks={frameState.externalLinks}
              myDesignMode
              saveModifications={handlePaintingChange}
            />
          </Box>
          <Divider
            className={clsx(
              classes.marginBottom,
              classes.marginTop,
              classes.dividerStyle
            )}
          />
        </Box>
      </FrameFormContainer>
    );
};

export default FrameForm;
